import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";

import {
  ListViewControl,
  useControlGroupContext,
} from "../../api/ControlGroup";

import {
  useGetReportAdminOperationFilter,
  useGetReportAdminOperationList,
} from "../../api/ReportsApi";
import { addDays } from "date-fns";
import { ReportsAdminOperationsInput } from "../../api/Reports";

import Table from "./Table";
import { LoadedSince } from "../../shared-components/LoadedSince";
import { classNames } from "../../utils";
import PaginationInMemory from "../../shared-components/PaginationInMemory";
import { SearchIcon } from "../../shared-components/icons";
import InputField from "../../shared-components/InputField";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { Modal } from "../../shared-components/Modal";
import FilterComponent from "../../shared-components/Filter";
import CompletedTableFilter from "../admin-completed/CompletedTableFilter";
import { getFilterValues, hasFilterValues } from "../admin-completed/AdminUtil";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { StatusFilter } from "./OperationStatusFilter";
import { WORKFLOW_STEP_STATUS_ENUM } from "../../api/Workflows";

function getCurrentDay(end: boolean = false) {
  let today = new Date();
  if (end) {
    today = addDays(today, 2);
  } else {
    today = addDays(today, -30);
  }
  return today.toISOString();
}

const initialFilter = {
  behavior_kit_uuid: [],
  blueprint_uuid: [],
  client_report_id: [],
  min_due_date: getCurrentDay(),
  max_due_date: getCurrentDay(true),
};

const OperationView = () => {
  const {
    isLoading: loadingCompleted,
    data: completedData,
    mutate: getCompletedList,
    error: errorGettingOperationList,
  } = useGetReportAdminOperationList();

  const {
    isLoading: loadingFilter,
    data: filterData,
    mutate: getFilterData,
    error: errorGettingFilterData,
  } = useGetReportAdminOperationFilter();

  const pageSize = 25;
  const [total, setTotal] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<number | undefined>(undefined);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [backup, setBackup] = useState<ListViewControl[] | null>(null);
  const [orderBy, setOrderBy] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  // const [selectedDate, setSelectedDate] = useState<Date>(new Date()); /// IEJ: enable once the date filter is implemented
  const [searchName, setSearchName] = useState("");
  const [prevSearchName, setPrevSearchName] = useState("");
  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const { controlGroups, setControlGroups, getControlByName } =
    useControlGroupContext();
  const [filter, setFilter] = useState<{ [key: string]: string[] | string }>(
    initialFilter
  );

  const [selectedStatus, setSelectedStatus] = useState<
    WORKFLOW_STEP_STATUS_ENUM[]
  >([]);

  function fetchCompletedListBaseProps(
    newFilter?: {
      company_uuid?: string[];
      behavior_kit_uuid?: string[];
      blueprint_uuid?: string[];
      client_report_id?: string[];
      boolean_search?: string[];
      min_due_date?: string;
      max_due_date?: string;
      workflow_step_name_csv?: string;
    },
    initialSearchName?: boolean
  ) {
    const fetchAttr: ReportsAdminOperationsInput = {
      page_size: pageSize,
      page: page - 1,
    };

    // check if there is at least one key in the newFilter object
    let filterCount = 0;

    if (newFilter) {
      const keys = Object.keys(newFilter);
      if (keys.length > 0) {
        filterCount = keys.reduce((acc, key) => {
          return newFilter[key as keyof typeof newFilter]?.length || 0 + acc;
        }, 0);
      }
    }

    if (filterCount > 0) {
      fetchAttr.filter = {
        ...newFilter,
      };
    } else {
      fetchAttr.filter = {};
    }

    const filterToSend = newFilter ? newFilter : filter;

    if (filterToSend["company_uuid"]?.length === 0) {
      delete filterToSend["company_uuid"];
    }
    if (searchName && !initialSearchName) {
      filterToSend["boolean_search"] = [searchName];
    } else {
      delete filterToSend["boolean_search"];
    }

    if (filterToSend["workflow_step_name_csv"]?.length === 0) {
      delete filterToSend["workflow_step_name_csv"];
    }
    fetchAttr.filter = {
      ...filterToSend,
    };
    getCompletedList(fetchAttr);
    getFilterData(filterToSend);
  }

  const fetchCompletedList = useCallback(() => {
    fetchCompletedListBaseProps();
    setControlGroups([
      { name: "company_uuid", controls: { direction: "", filters: [] } },
      { name: "behavior_kit_uuid", controls: { direction: "", filters: [] } },
      { name: "client_report_id", controls: { direction: "", filters: [] } },
      { name: "blueprint_uuid", controls: { direction: "", filters: [] } },
    ]);
    // eslint-disable-next-line
  }, [getCompletedList, getFilterData, orderBy]);

  useEffect(() => {
    fetchCompletedList();
  }, [fetchCompletedList]);

  useEffect(() => {
    if (completedData) {
      setTotal(completedData?.total_rows ?? 1);
      setLastUpdated(new Date().getTime());
    }
  }, [completedData]);

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(Number(searchParams.get("page")));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCompletedListBaseProps();
    // eslint-disable-next-line
  }, [page]);

  const hasFilters = (name: string): boolean => {
    const control = controlGroups.find((control) => control.name === name);
    return control
      ? control.controls.filters?.length > 0 ||
          control.controls.direction?.trim() !== ""
      : false;
  };

  const openModalByName = (name: string) => {
    setBackup(structuredClone(controlGroups));
    setOpenModal(name);
  };

  const handleOrderByChange = (name: string, action: "add" | "remove") => {
    setOrderBy((prevOrderBy) => {
      const newSet = new Set(prevOrderBy);
      action === "add" ? newSet.add(name) : newSet.delete(name);
      return newSet;
    });
  };

  const handleClearAll = () => {
    setControlGroups([
      { name: "company_uuid", controls: { direction: "", filters: [] } },
      { name: "behavior_kit_uuid", controls: { direction: "", filters: [] } },
      { name: "client_report_id", controls: { direction: "", filters: [] } },
      { name: "blueprint_uuid", controls: { direction: "", filters: [] } },
    ]);
    setFilter(initialFilter);
    setPage(1);
    setSearchName("");
    setPrevSearchName("");
    setSelectedStatus([]);
    fetchCompletedListBaseProps(initialFilter, true);
  };

  const handleClose = () => {
    if (backup) {
      setControlGroups(backup);
    }
    setOpenModal(null);
  };

  const handleSaveFilter = (
    page: number,
    controlSaved?: string,
    action: "add" | "remove" = "add"
  ) => {
    if (controlSaved) {
      if (getControlByName(controlSaved)?.controls.direction) {
        handleOrderByChange(controlSaved, action);
      }
    }

    searchParams.set("page", "1");
    navigate({ search: searchParams.toString() });
    const newFilter = {
      ...filter,
    };
    if (controlSaved && action === "remove") {
      newFilter[controlSaved] = [];
    }

    if (controlSaved && action === "add") {
      newFilter[controlSaved] =
        controlGroups.find((group) => group.name === controlSaved)?.controls
          .filters ?? [];
    }

    setFilter(newFilter);
    fetchCompletedListBaseProps(newFilter);
    setOpenModal(null);
  };

  const tableHeader = [
    { name: "NAME / COMPANY / ID" },
    { name: "PARENT COMPANY" },
    { name: "STATUS" },
    { name: "CHECK TYPE / BEHAVIOR KIT" },
    { name: "DATE ADDED" },
    { name: "DUE DATE" },
    { name: "CLIENT REF ID" },
    { name: "ACTIONS" },
  ];

  tableHeader.push({ name: "DELETE" });

  function clearCache() {
    fetchCompletedListBaseProps();
  }
  function handleInputAction() {
    const newFilter = {
      ...filter,
    };

    if (prevSearchName === searchName) {
      return;
    }
    setPrevSearchName(searchName);

    if (searchName) {
      newFilter["boolean_search"] = [searchName];
    } else {
      delete newFilter["boolean_search"];
    }
    fetchCompletedListBaseProps(newFilter);
  }

  function handleStatusChange(status: WORKFLOW_STEP_STATUS_ENUM[]) {
    setSelectedStatus(status);
    const statusString = status.join(",");
    const newFilter = {
      ...filter,
      workflow_step_name_csv: statusString,
    };
    setFilter(newFilter);
    fetchCompletedListBaseProps(newFilter);
  }

  const hasCompanyFilter = hasFilterValues(controlGroups, "company_uuid");

  const hasBehaviorKitFilter = hasFilterValues(
    controlGroups,
    "behavior_kit_uuid"
  );

  const hasClientReportFilter = hasFilterValues(
    controlGroups,
    "client_report_id"
  );

  const hasCheckTypeFilter = hasFilterValues(controlGroups, "blueprint_uuid");

  const companyFilterValues = getFilterValues(
    controlGroups,
    "company_uuid",
    filterData
  );

  const behaviorKitFilterValues = getFilterValues(
    controlGroups,
    "behavior_kit_uuid",
    filterData
  );

  const checkTypeFilterValues = getFilterValues(
    controlGroups,
    "blueprint_uuid",
    filterData
  );

  const clientReportFilterValues = getFilterValues(
    controlGroups,
    "client_report_id",
    filterData
  );

  return (
    <>
      <div
        className={classNames(
          "relative flex flex-row flex-wrap gap-2 mb-2 bg-white p-4 rounded-lg border border-gray-200 items-center",
          loadingFilter ? "opacity-50 pointer-events-none" : ""
        )}
      >
        <div className="absolute right-2 top-[-1.5rem] w-48 h-3">
          <LoadedSince
            reportData={{ timestamp: lastUpdated }}
            onClear={clearCache}
          />
        </div>
        <>
          <CompletedTableFilter
            hasFilter={hasCompanyFilter}
            filterValues={companyFilterValues}
            setOpenModal={setOpenModal}
            title="Please select a Company"
            modalKey="company_uuid"
          />

          <CompletedTableFilter
            hasFilter={hasCheckTypeFilter}
            filterValues={checkTypeFilterValues}
            setOpenModal={setOpenModal}
            title="Please select a Check Type"
            modalKey="blueprint_uuid"
          />
          <CompletedTableFilter
            hasFilter={hasBehaviorKitFilter}
            filterValues={behaviorKitFilterValues}
            setOpenModal={setOpenModal}
            title="Please select a Behavior Kit"
            modalKey="behavior_kit_uuid"
          />
          <CompletedTableFilter
            hasFilter={hasClientReportFilter}
            filterValues={clientReportFilterValues}
            setOpenModal={setOpenModal}
            title="Please select a Client Reference ID"
            modalKey="client_report_id"
          />
          <StatusFilter
            onChange={(statuses) => {
              handleStatusChange(statuses);
            }}
            selectedStatus={selectedStatus}
          />
          <div className="flex flex-row w-80 items-center relative">
            <InputField
              handleChange={(e) => {
                setSearchName(e.target.value);
              }}
              handleBlur={handleInputAction}
              handleEnter={handleInputAction}
              name={"name"}
              placeholder="Search Name"
              label={""}
              className="w-full pr-10"
              value={searchName}
            />
            <SearchIcon className="w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <button
            className="border-none underline text-sm text-gray-500  ml-3 focus:outline-none"
            onClick={handleClearAll}
          >
            Clear All
          </button>
          {(errorGettingFilterData || errorGettingOperationList) && (
            <div className="flex flex-row items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
              <div className="text-red-500">Couldn't get filters</div>
            </div>
          )}
        </>
        {loadingFilter && <LoadingSpinner />}
      </div>
      <div>
        <Table
          isLoading={loadingCompleted}
          header={tableHeader}
          openFilter={openModalByName}
          hasFilters={hasFilters}
          data={completedData?.report_summaries ?? []}
          hasError={!!errorGettingOperationList || !!errorGettingFilterData}
          onChange={() => {
            fetchCompletedListBaseProps();
          }}
        />
      </div>
      {!loadingCompleted && (
        <PaginationInMemory
          currentPage={page}
          pageSize={pageSize}
          totalCount={total}
          pageChange={(newPageIdx) => {
            setPage(newPageIdx);
          }}
        />
      )}

      {filterData &&
        openModal &&
        filterData[openModal] &&
        controlGroups.map((group, id) => (
          <Modal
            key={id}
            open={openModal === group.name}
            width="w-[600px]"
            onClose={handleClose}
          >
            <FilterComponent
              controlGroup={group}
              date={false}
              parentList={filterData[openModal]}
              loading={loadingFilter}
              saveFilter={handleSaveFilter}
              cleared={() => {
                handleSaveFilter(1, group.name, "remove");
              }}
              showSortHeader={false}
              canceled={handleClose}
            />
          </Modal>
        ))}
    </>
  );
};

export default OperationView;
